import { type NilRankingResponse } from '@on3/api';
import { On3IconArrow, On3IconDownArrow, On3IconLockPremium } from '@on3/icons';
import {
  SocialIcon,
  type SocialIconType,
} from '@on3/ui-lib/components/SocialIcon';
import { useAuth } from '@on3/ui-lib/index';
import {
  formatCurrency,
  formatMillions,
} from '@on3/ui-lib/src/utils/formatters';
import { getAbbreviatedClassRank } from '@on3/ui-lib/utils/getAbbreviatedClassRank';
import clsx from 'clsx';
import { SportIcon, type SportName } from 'components/Icons/SportIcon';
import { UndecidedIcon } from 'components/Icons/UndecidedIcon';
import { On3Avatar } from 'components/Image/On3Avatar';
import { TeamAvatar } from 'components/Image/TeamAvatar';
import { VerifiedEliteAthlete } from 'components/UI/AthleteVerified/VerifiedEliteAthlete';
import { StarRating } from 'components/UI/StarRating/StarRating';
import { Text } from 'components/UI/Typography/Text';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { socialLink } from 'utils/social';

import styles from './NilPlayerRankingItem.module.scss';

export const valuationChangeType = (change: number) => {
  if (change) {
    if (change > 0) {
      return 'positive';
    } else if (change < 0) {
      return 'negative';
    }

    return 'none';
  }

  return 'none';
};

interface INilPlayerRankingItemProps extends NilRankingResponse {
  indexRank?: number;
}
export const NilPlayerRankingItem = ({
  person,
  valuation,
  indexRank,
}: INilPlayerRankingItemProps) => {
  const { user } = useAuth();
  const { query } = useRouter();
  const { 'transfer-portal': transferPortal } = query;
  const sport = query.sport as string;
  const organizationType = query.organizationType as string;
  const isTransferPortal = transferPortal === 'true';
  const isHighSchool = organizationType === 'high-school';
  const isCollege = organizationType === 'college';
  const isSportWithRating = sport === 'football' || sport === 'basketball';
  const {
    status,
    defaultSport,
    defaultAsset,
    name,
    formattedHeight,
    weight,
    homeTownName,
    highSchool,
    classYear,
    classRank,
    positionAbbreviation,
    athleteVerified,
    rating,
    slug,
    predictions,
  } = person || {};

  const setChangeType = valuation?.valuationChange
    ? valuationChangeType(valuation?.valuationChange)
    : 'none';
  const isCommited = status?.isCommitted;
  const playerClassRank = classRank && getAbbreviatedClassRank(classRank);
  const isSigned = status?.isSigned;
  const isEnrolled = status?.isEnrolled;
  const isTransfer = status?.isTransfer;
  const teamSlug = status?.committedOrganizationSlug;
  const sportName: SportName = (defaultSport?.name as SportName) || '';
  const playerValuation = valuation?.valuation;
  const hasPredictions = !!predictions?.length;
  const showStatus = (isCommited && !isTransfer) || isSigned || isEnrolled;
  const showPredictions =
    hasPredictions && !isCommited && !isSigned && !isEnrolled;
  const showTransferStatus = !showPredictions && isTransfer && !showStatus;

  const utm_campaign = isHighSchool
    ? 'nil_high_school'
    : isTransferPortal
      ? 'nil_transfer_portal'
      : 'nil_college';

  const joinLink = `/subscribe/`;

  const params = new URLSearchParams({
    utm_source: 'nil',
    utm_medium: 'web',
    utm_campaign,
    utm_content: 'lock',
  });

  const utmLink = `${joinLink}?${params}`;

  return (
    <div className={styles.itemContainer}>
      <span className={styles.playerRank}>{indexRank}</span>
      <div className={styles.sportWrapper}>
        <SportIcon className={clsx(styles.sportIcon)} sportName={sportName} />
        <span className={styles.position}>{positionAbbreviation || '-'}</span>
      </div>
      <On3Avatar
        altText={defaultAsset?.altText}
        className={styles.playerAvatar}
        domain={defaultAsset?.domain}
        height={50}
        radiusFive
        source={defaultAsset?.source}
        width={50}
      />
      <div className={styles.name}>
        <Link href={`/rivals/${slug}/nil/`}>{name}</Link>
      </div>
      <div className={styles.details}>
        {(playerClassRank || classYear) && (
          <span className={styles.classYear}>
            {isCollege ? playerClassRank || classYear : classYear}/
          </span>
        )}
        <span>{formattedHeight ?? '-'}/</span>
        <span>{weight ?? '-'}</span>
      </div>
      <div className={styles.hometownContainer}>
        <Link
          className={styles.highSchool}
          href={`/high-school/${highSchool?.urlSlug}/`}
        >
          {highSchool?.name}
        </Link>

        {homeTownName && <span>({homeTownName})</span>}
      </div>
      {isSportWithRating && (
        <div className={styles.ratingWrapper}>
          <div className={styles.rating}>
            <StarRating
              fiveStarPlus={rating?.fiveStarPlus}
              hasBorder={false}
              isBoldRating={false}
              isConsensus
              rating={rating?.consensusRating}
              stars={rating?.consensusStars}
            />
          </div>
          {isHighSchool && (
            <div className={styles.rankingsWrapper}>
              <span className={styles.rankItem}>
                <span>{rating?.consensusNationalRank || '-'}</span>
                <span>Natl.</span>
              </span>
              <span className={styles.rankItem}>
                <span>{rating?.consensusPositionRank || '-'}</span>
                <span>Pos.</span>
              </span>
              <span className={styles.rankItem}>
                <span>{rating?.consensusStateRank || '-'}</span>
                <span>St.</span>
              </span>
            </div>
          )}
        </div>
      )}
      {showStatus && (
        <div className={styles.statusWrapper}>
          <div className={styles.statusContainer}>
            <div className={styles.statusItem}>
              <Link
                href={`/college/${teamSlug}/${sport.toLowerCase()}/${status?.recruitmentYear}/industry-comparison-commits/`}
              >
                <TeamAvatar
                  className={styles.statusLogo}
                  height={30}
                  slug={teamSlug}
                  src={status?.committedOrganizationAssetUrl}
                  width={30}
                />
              </Link>
              {isCommited && !isSigned && !isEnrolled && !isTransfer && (
                <span className={styles.statusText}>Commit</span>
              )}
            </div>
          </div>
        </div>
      )}
      {showPredictions && (
        <div className={styles.predictionsWrapper}>
          <div className={styles.predictionsContainer}>
            {predictions?.slice(0, 2).map((org) => {
              return (
                <div className={styles.prediction} key={org.key}>
                  <TeamAvatar
                    className={styles.predictionLogo}
                    height={25}
                    slug={org?.organization?.slug}
                    src={org?.organization?.assetUrl?.url}
                    width={25}
                  />

                  {org.percent && (
                    <Text className={styles.rpmPercent}>
                      {Math.round(org.percent)}
                    </Text>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {showTransferStatus && (
        <div className={styles.transferWrapper}>
          <div className={styles.transferContainer}>
            <TeamAvatar
              className={styles.transferLogo}
              height={25}
              slug={status.transferredFromOrganizationSlug}
              src={status.transferredFromOrganizationAssetUrl}
              width={25}
            />
            <On3IconArrow className={styles.arrow} />
            {status.committedOrganizationAssetUrl ? (
              <TeamAvatar
                className={styles.transferLogo}
                height={25}
                slug={status.committedOrganizationSlug}
                src={status.committedOrganizationAssetUrl}
                width={25}
              />
            ) : (
              <UndecidedIcon
                className={styles.undecided}
                height={25}
                width={25}
              />
            )}
          </div>
        </div>
      )}

      <div className={styles.valuationWrapper}>
        <div className={styles.valuationContainer}>
          <span className={styles.valuationCurrency}>
            {playerValuation ? formatCurrency(playerValuation) : '-'}
          </span>

          {setChangeType !== 'none' &&
            valuation?.valuationChange &&
            Math.abs(valuation.valuationChange) > 10000 && (
              <div
                className={clsx(
                  styles.valuationChangeText,
                  styles[setChangeType],
                )}
              >
                {formatCurrency(Math.abs(valuation.valuationChange))}
              </div>
            )}
        </div>
        {!!valuation.rosterValue && user?.has && (
          <div className={styles.rosterValue}>
            {formatCurrency(Math.abs(valuation.rosterValue))}
          </div>
        )}

        {!user?.has && (
          <Link href={utmLink}>
            <On3IconLockPremium />
          </Link>
        )}
      </div>

      <div className={styles.socialWrapper}>
        <div className={styles.socialContainer}>
          {valuation?.socialValuations?.map((social, key) => {
            const socialType = social?.profile?.type;

            if (social?.followers) {
              return (
                <div className={styles.socialItem} key={key}>
                  <Link
                    href={socialLink(
                      socialType as SocialIconType,
                      social.profile?.handle,
                    )}
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                  >
                    {socialType && (
                      <SocialIcon type={socialType as SocialIconType} />
                    )}
                  </Link>

                  <span className={styles.socialItemText}>
                    {formatMillions(social.followers)}
                  </span>
                </div>
              );
            }

            return null;
          })}
        </div>
      </div>
      {/* TODO: Recruits - claim profile */}
      {athleteVerified ? (
        <VerifiedEliteAthlete className={styles.verifiedAthlete} />
      ) : null}
    </div>
  );
};
